import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, HeartHandshake, MessageSquare, ArrowRight, Scale, Mail } from "lucide-react";

const SLIDES = [
  {
    icon: MessageSquare,
    title: "Répondez intelligemment",
    desc: "Transformez n'importe quel commentaire en réponse argumentée.",
    glow: "rgba(212,175,55,0.35)",
  },
  {
    icon: Scale,
    title: "Connaissez vos droits",
    desc: "Des fiches claires face au racisme, aux discriminations et aux injustices — lois, recours et démarches.",
    glow: "rgba(212,175,55,0.30)",
  },
  {
    icon: BookOpen,
    title: "Découvrez une bibliothèque afro-descendante",
    desc: "Livres, auteurs, mémoire, histoire, culture.",
    glow: "rgba(212,175,55,0.30)",
  },
  {
    icon: Mail,
    title: "Générez vos courriers",
    desc: "Un assistant qui rédige pour vous des courriers professionnels, prêts à envoyer et exportables en PDF.",
    glow: "rgba(212,175,55,0.32)",
  },
  {
    icon: HeartHandshake,
    title: "Vous n'êtes plus seul.",
    desc: "Professionnels, psychologues, juristes, créateurs de contenu et ressources réunis dans un seul endroit.",
    glow: "rgba(212,175,55,0.32)",
  },
];

export default function Onboarding({ onDone }) {
  const [i, setI] = useState(0);
  const slide = SLIDES[i];
  const Icon = slide.icon;
  const last = i === SLIDES.length - 1;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col relative overflow-hidden">
      <motion.div
        key={`glow-${i}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(700px circle at 50% 30%, ${slide.glow}, transparent 60%)` }}
      />

      {/* header */}
      <div className="relative flex items-center justify-between px-6 pt-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center">
            <span className="text-black font-bold text-sm font-display">B</span>
          </div>
          <span className="font-display tracking-tight">BIZARRE <span className="gold-text">AI</span></span>
        </div>
        <button data-testid="onboarding-skip" onClick={() => onDone("register")}
          className="text-white/40 text-sm hover:text-white transition-colors">Passer</button>
      </div>

      {/* content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-8 text-center max-w-lg mx-auto w-full">
        <AnimatePresence mode="wait">
          <motion.div key={i}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center">
            <motion.div
              initial={{ scale: 0.7, rotate: -8 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="w-24 h-24 rounded-3xl bg-white/[0.04] border border-gold/30 flex items-center justify-center mb-10 shadow-2xl shadow-black">
              <Icon size={40} strokeWidth={1.5} className="text-gold" />
            </motion.div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight font-light mb-4">{slide.title}</h1>
            <p className="text-white/50 text-base leading-relaxed">{slide.desc}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* footer */}
      <div className="relative px-8 pb-10 max-w-lg mx-auto w-full">
        <div className="flex items-center justify-center gap-2 mb-8">
          {SLIDES.map((_, idx) => (
            <motion.div key={idx} animate={{ width: idx === i ? 28 : 8, opacity: idx === i ? 1 : 0.3 }}
              transition={{ duration: 0.3 }}
              className={`h-2 rounded-full ${idx === i ? "gold-gradient" : "bg-white/40"}`} />
          ))}
        </div>

        {last ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="space-y-3">
            <button data-testid="onboarding-register" onClick={() => onDone("register")}
              className="w-full gold-gradient text-black rounded-full py-3.5 font-medium hover:opacity-90 transition-opacity">
              Commencer
            </button>
            <button data-testid="onboarding-login" onClick={() => onDone("login")}
              className="w-full bg-transparent border border-white/20 text-white rounded-full py-3.5 font-medium hover:bg-white/[0.06] transition-colors">
              J'ai déjà un compte
            </button>
          </motion.div>
        ) : (
          <button data-testid="onboarding-next" onClick={() => setI(i + 1)}
            className="w-full gold-gradient text-black rounded-full py-3.5 font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
            Continuer <ArrowRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
